/**
 * Permission System - Core Types and Utilities
 * 
 * Defines the granular permission system for modules and actions
 */

// ============================================
// Permission System Constants
// ============================================

/**
 * Available modules in the system
 */
export const MODULES = {
  ALBARANES: 'albaranes',
  PARTES_TRABAJO: 'partes_trabajo',
  PRL: 'prl',
  SOLICITUDES: 'solicitudes',
  DOCUMENTOS: 'documentos', // Vista general de documentos
  CONTACTOS: 'contactos',
  USUARIOS: 'usuarios',
  EMPRESAS: 'empresas',
  CONFIGURACION: 'configuracion',
  REPORTES: 'reportes',
  SUPER_ADMIN: 'super_admin'
};

/**
 * Available actions for each module
 */
export const ACTIONS = {
  READ: 'read',
  CREATE: 'create',
  UPDATE: 'update',
  DELETE: 'delete',
  MANAGE: 'manage', // Full control (create, read, update, delete)
  EXPORT: 'export',
  IMPORT: 'import'
};

/**
 * Module-specific actions mapping
 */
export const MODULE_ACTIONS = {
  [MODULES.ALBARANES]: [
    ACTIONS.READ,
    ACTIONS.CREATE,
    ACTIONS.UPDATE,
    ACTIONS.DELETE,
    ACTIONS.EXPORT
  ],
  [MODULES.PARTES_TRABAJO]: [
    ACTIONS.READ,
    ACTIONS.CREATE,
    ACTIONS.UPDATE,
    ACTIONS.DELETE,
    ACTIONS.EXPORT
  ],
  [MODULES.SOLICITUDES]: [
    ACTIONS.READ,
    ACTIONS.CREATE,
    ACTIONS.UPDATE,
    ACTIONS.DELETE
  ],
  [MODULES.DOCUMENTOS]: [
    ACTIONS.READ,
    ACTIONS.EXPORT
  ],
  [MODULES.CONTACTOS]: [
    ACTIONS.READ,
    ACTIONS.CREATE,
    ACTIONS.UPDATE,
    ACTIONS.DELETE,
    ACTIONS.IMPORT,
    ACTIONS.EXPORT
  ],
  [MODULES.USUARIOS]: [
    ACTIONS.READ,
    ACTIONS.CREATE,
    ACTIONS.UPDATE,
    ACTIONS.DELETE
  ],
  [MODULES.EMPRESAS]: [
    ACTIONS.READ,
    ACTIONS.CREATE,
    ACTIONS.UPDATE,
    ACTIONS.DELETE
  ],
  [MODULES.CONFIGURACION]: [
    ACTIONS.READ,
    ACTIONS.UPDATE
  ],
  [MODULES.REPORTES]: [
    ACTIONS.READ,
    ACTIONS.EXPORT
  ],
  [MODULES.SUPER_ADMIN]: [
    ACTIONS.MANAGE
  ]
};

/**
 * Route to module mapping
 */
export const ROUTE_MODULE_MAP = {
  '/upload': [MODULES.ALBARANES, MODULES.PARTES_TRABAJO],
  '/docs': [MODULES.DOCUMENTOS],
  '/solicitudes': [MODULES.SOLICITUDES],
  '/solicitar-parte': [MODULES.SOLICITUDES],
  '/historial-solicitudes': [MODULES.SOLICITUDES],
  '/admin': [MODULES.USUARIOS, MODULES.CONFIGURACION],
  '/super-admin': [MODULES.SUPER_ADMIN],
  '/super-admin/companies': [MODULES.EMPRESAS],
  '/super-admin/users': [MODULES.USUARIOS],
  '/super-admin/stats': [MODULES.REPORTES]
};

/**
 * Predefined permission profiles
 */
export const PERMISSION_PROFILES = {
  TECNICO_ALBARANES: {
    id: 'tecnico-albaranes',
    name: 'Técnico Albaranes',
    description: 'Solo puede gestionar albaranes',
    permissions: [
      {
        module: MODULES.ALBARANES,
        actions: [ACTIONS.READ, ACTIONS.CREATE, ACTIONS.UPDATE]
      },
      {
        module: MODULES.CONTACTOS,
        actions: [ACTIONS.READ, ACTIONS.CREATE, ACTIONS.UPDATE]
      }
    ]
  },
  SUPERVISOR_PARTES: {
    id: 'supervisor-partes',
    name: 'Supervisor Partes de Trabajo',
    description: 'Gestiona partes de trabajo y solicitudes',
    permissions: [
      {
        module: MODULES.PARTES_TRABAJO,
        actions: [ACTIONS.READ, ACTIONS.CREATE, ACTIONS.UPDATE]
      },
      {
        module: MODULES.SOLICITUDES,
        actions: [ACTIONS.READ, ACTIONS.CREATE, ACTIONS.UPDATE]
      },
      {
        module: MODULES.CONTACTOS,
        actions: [ACTIONS.READ, ACTIONS.CREATE, ACTIONS.UPDATE]
      }
    ]
  },
  SOLO_CONSULTA: {
    id: 'solo-consulta',
    name: 'Solo Consulta',
    description: 'Solo puede ver documentos',
    permissions: [
      {
        module: MODULES.DOCUMENTOS,
        actions: [ACTIONS.READ]
      },
      {
        module: MODULES.ALBARANES,
        actions: [ACTIONS.READ]
      },
      {
        module: MODULES.PARTES_TRABAJO,
        actions: [ACTIONS.READ]
      },
      {
        module: MODULES.SOLICITUDES,
        actions: [ACTIONS.READ]
      }
    ]
  },
  ADMIN_COMPLETO: {
    id: 'admin-completo',
    name: 'Administrador Completo',
    description: 'Acceso completo a todos los módulos',
    permissions: [
      {
        module: MODULES.ALBARANES,
        actions: [ACTIONS.READ, ACTIONS.CREATE, ACTIONS.UPDATE, ACTIONS.DELETE, ACTIONS.EXPORT]
      },
      {
        module: MODULES.PARTES_TRABAJO,
        actions: [ACTIONS.READ, ACTIONS.CREATE, ACTIONS.UPDATE, ACTIONS.DELETE, ACTIONS.EXPORT]
      },
      {
        module: MODULES.SOLICITUDES,
        actions: [ACTIONS.READ, ACTIONS.CREATE, ACTIONS.UPDATE, ACTIONS.DELETE]
      },
      {
        module: MODULES.DOCUMENTOS,
        actions: [ACTIONS.READ, ACTIONS.EXPORT]
      },
      {
        module: MODULES.CONTACTOS,
        actions: [ACTIONS.READ, ACTIONS.CREATE, ACTIONS.UPDATE, ACTIONS.DELETE, ACTIONS.IMPORT, ACTIONS.EXPORT]
      },
      {
        module: MODULES.USUARIOS,
        actions: [ACTIONS.READ, ACTIONS.CREATE, ACTIONS.UPDATE, ACTIONS.DELETE]
      },
      {
        module: MODULES.CONFIGURACION,
        actions: [ACTIONS.READ, ACTIONS.UPDATE]
      },
      {
        module: MODULES.REPORTES,
        actions: [ACTIONS.READ, ACTIONS.EXPORT]
      }
    ]
  }
};

// ============================================
// Permission Utility Functions
// ============================================

/**
 * Calculate effective permissions from profile and custom permissions
 * @param {Object} profile - Permission profile
 * @param {Array} customPermissions - Additional custom permissions
 * @returns {Array} Combined permissions array
 */
export function calculateEffectivePermissions(profile, customPermissions = []) {
  const profilePermissions = profile?.permissions || [];
  const effectivePermissions = [...profilePermissions];
  
  // Add custom permissions, merging actions for same modules
  customPermissions.forEach(customPerm => {
    const existingIndex = effectivePermissions.findIndex(
      perm => perm.module === customPerm.module
    );
    
    if (existingIndex >= 0) {
      // Merge actions, removing duplicates
      const existingActions = effectivePermissions[existingIndex].actions;
      const newActions = [...new Set([...existingActions, ...customPerm.actions])];
      effectivePermissions[existingIndex].actions = newActions;
    } else {
      // Add new module permission
      effectivePermissions.push(customPerm);
    }
  });
  
  return effectivePermissions;
}

/**
 * Check if user has permission for specific module and action
 * @param {Array} permissions - User's effective permissions
 * @param {string} module - Module to check
 * @param {string} action - Action to check
 * @returns {boolean} True if user has permission
 */
export function hasPermission(permissions, module, action) {
  if (!permissions || !Array.isArray(permissions)) return false;
  
  const modulePermission = permissions.find(perm => perm.module === module);
  if (!modulePermission) return false;
  
  return modulePermission.actions.includes(action) || 
         modulePermission.actions.includes(ACTIONS.MANAGE);
}

/**
 * Check if user has access to any action in a module
 * @param {Array} permissions - User's effective permissions
 * @param {string} module - Module to check
 * @returns {boolean} True if user has any access to module
 */
export function hasModuleAccess(permissions, module) {
  if (!permissions || !Array.isArray(permissions)) return false;
  
  const modulePermission = permissions.find(perm => perm.module === module);
  return modulePermission && modulePermission.actions.length > 0;
}

/**
 * Check if user can access a specific route
 * @param {Array} permissions - User's effective permissions
 * @param {string} route - Route to check
 * @returns {boolean} True if user can access route
 */
export function canAccessRoute(permissions, route) {
  const requiredModules = ROUTE_MODULE_MAP[route];
  if (!requiredModules) return true; // Route not protected
  
  // User needs access to at least one of the required modules
  return requiredModules.some(module => hasModuleAccess(permissions, module));
}

/**
 * Get all modules user has access to
 * @param {Array} permissions - User's effective permissions
 * @returns {Array} Array of module names user can access
 */
export function getAccessibleModules(permissions) {
  if (!permissions || !Array.isArray(permissions)) return [];
  
  return permissions
    .filter(perm => perm.actions.length > 0)
    .map(perm => perm.module);
}

/**
 * Get all actions user can perform on a module
 * @param {Array} permissions - User's effective permissions
 * @param {string} module - Module to check
 * @returns {Array} Array of actions user can perform
 */
export function getModuleActions(permissions, module) {
  if (!permissions || !Array.isArray(permissions)) return [];
  
  const modulePermission = permissions.find(perm => perm.module === module);
  return modulePermission ? modulePermission.actions : [];
}

/**
 * Validate permission structure
 * @param {Object} permission - Permission object to validate
 * @returns {Object} Validation result with isValid and errors
 */
export function validatePermission(permission) {
  const errors = [];
  
  if (!permission.module) {
    errors.push('Module is required');
  } else if (!Object.values(MODULES).includes(permission.module)) {
    errors.push(`Invalid module: ${permission.module}`);
  }
  
  if (!permission.actions || !Array.isArray(permission.actions)) {
    errors.push('Actions must be an array');
  } else {
    const validActions = MODULE_ACTIONS[permission.module] || [];
    const invalidActions = permission.actions.filter(
      action => !validActions.includes(action)
    );
    if (invalidActions.length > 0) {
      errors.push(`Invalid actions for module ${permission.module}: ${invalidActions.join(', ')}`);
    }
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
}

/**
 * Validate permission profile
 * @param {Object} profile - Profile to validate
 * @returns {Object} Validation result with isValid and errors
 */
export function validatePermissionProfile(profile) {
  const errors = [];
  
  if (!profile.id) errors.push('Profile ID is required');
  if (!profile.name) errors.push('Profile name is required');
  if (!profile.permissions || !Array.isArray(profile.permissions)) {
    errors.push('Profile permissions must be an array');
  } else {
    profile.permissions.forEach((perm, index) => {
      const validation = validatePermission(perm);
      if (!validation.isValid) {
        errors.push(`Permission ${index + 1}: ${validation.errors.join(', ')}`);
      }
    });
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
}

/**
 * Check if permissions exceed company limits
 * @param {Array} permissions - Permissions to check
 * @param {Array} companyLimits - Company permission limits
 * @returns {Object} Validation result with isValid and violations
 */
export function checkCompanyLimits(permissions, companyLimits) {
  if (!companyLimits || !Array.isArray(companyLimits)) {
    return { isValid: true, violations: [] };
  }
  
  const violations = [];
  
  permissions.forEach(permission => {
    const limit = companyLimits.find(limit => limit.module === permission.module);
    if (!limit) {
      violations.push(`Module ${permission.module} not allowed for this company`);
      return;
    }
    
    const unauthorizedActions = permission.actions.filter(
      action => !limit.actions.includes(action)
    );
    
    if (unauthorizedActions.length > 0) {
      violations.push(
        `Actions not allowed for module ${permission.module}: ${unauthorizedActions.join(', ')}`
      );
    }
  });
  
  return {
    isValid: violations.length === 0,
    violations
  };
}